/* eslint-disable consistent-return */
/* eslint-disable camelcase */
/**
 * @file loginView.jsx
 * @author Fernando Ruano
 * @description StepTwo View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FileTextOutlined } from '@ant-design/icons';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import LoginTemplate from '@nephro/components/LoginTemplate';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { privacy } from '@nephro/commons/helpers/string-helper';

// Styles
import styles from './styles.module.css';

function stepFourView({ loading, authActions }) {
  const [firstClick, setFirstClick] = React.useState(false);
  const [fullScrolled, setFullScrolled] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const listInnerRef = React.useRef();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const lastAuthPath = getStorageItem('lastAuthPath');
  const { activatePatientByRegister } = authActions;

  const onNavigationBack = () => {
    history.replace(lastAuthPath);
  };

  React.useEffect(() => {
    if (getStorageItem('user')) {
      onNavigationBack();
    }
  }, [listInnerRef]);

  React.useEffect(() => {
    if (listInnerRef.current) {
      const { scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollHeight <= clientHeight) setFullScrolled(true);
    }
  }, [firstClick]);

  const onScroll = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 1) setFullScrolled(true);
    }
  };

  const onFirstClick = () => setFirstClick(true);

  const onFinishRegistration = () => {
    const data = {
      email: location?.state?.email,
      identificationDocument: location?.state?.document,
      documentType: location?.state?.docType
    };

    activatePatientByRegister(data, () =>
      notification.success({
        message: translate('registration.notification'),
        description: translate('registration.succesRegister'),
        duration: 5
      })
    );
    history.push('/login');
  };

  return (
    <LoginTemplate
      loading={loading}
      showTitle={!firstClick}
      showContent={!firstClick}
      title="registration.lastStep"
      textContent="registration.lastStepContent2">
      <>
        {!firstClick ? (
          <ButtonFilled
            key="2"
            type="blue"
            onActionClick={onFirstClick}
            textButton={translate('registration.privacy')}
            classStyles={[styles.actionButton].join(' ')}
            iconComponent={<FileTextOutlined style={{ marginTop: -2 }} />}
          />
        ) : (
          <div className="-ml-4 -mr-3 lg:-mr-6 lg:-ml-7">
            <section
              className={styles.scrollFrame}
              onScroll={() => onScroll()}
              ref={listInnerRef}>
              <td
                dangerouslySetInnerHTML={{
                  __html: privacy
                }}
              />
            </section>
            <div className="flex justify-center items-center ml-4 mr-1">
              <Checkbox
                checked={checked}
                disabled={!fullScrolled}
                onChange={(e) => setChecked(e.target.checked)}>
                {translate('registration.acceptPrivacy')}
              </Checkbox>
            </div>
            <div className="flex flex-row justify-between mb-7 mt-5">
              <Button
                onClick={onNavigationBack}
                type="link"
                className={styles.stepThreeButton}>
                {translate('commons.cancel')}
              </Button>
              <Button
                onClick={onFinishRegistration}
                disabled={!checked}
                type={checked && 'primary'}
                className={styles.stepThreeButton}>
                {translate('registration.registerMe')}
              </Button>
            </div>
          </div>
        )}
      </>
    </LoginTemplate>
  );
}

/**
 * Component definition of proptypes.
 */
stepFourView.propTypes = {
  authActions: PropTypes.shape({
    activatePatientByRegister: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool.isRequired
};

export default stepFourView;
